import { PERMISSIONS } from 'api/auth/constants';
import { PERMISSION_TYPE } from 'hooks/useRBAC';
import { ENTITY } from 'routes/pages/AuthorizedStack/UserManual/helpers';
import { TObject } from 'utils/types';

import {
  IManualContent,
  IManualImage,
  IManualMedia,
  IManualSection,
  IManualSectionConfiguration,
  IManualTable,
  IUserManual,
  MANUAL_COMPONENT,
} from './types';

const normalizeImage = (data?: TObject): IManualImage | undefined => {
  if (!data) {
    return;
  }
  return {
    alternativeText: (data?.alternativeText as string) || '',
    caption: (data?.caption as string) || '',
    createdAt: (data?.createdAt as string) || '',
    ext: (data?.ext as string) || '',
    folderPath: (data?.folderPath as string) || '',
    formats: (data?.formats as string) || '',
    hash: (data?.hash as string) || '',
    height: (data?.height as number) || 0,
    id: (data?.id as number) || 0,
    mime: (data?.mime as string) || '',
    name: (data?.id as string) || '',
    previewUrl: (data?.previewUrl as string) || '',
    provider: (data?.provider as string) || '',
    provider_metadata: (data?.provider_metadata as string) || '',
    size: (data?.size as number) || 0,
    updatedAt: (data?.updatedAt as string) || '',
    url: (data?.url as string) || '',
    width: (data?.width as number) || 0,
  };
};

type IMediaData = TObject & { image: TObject };

const normalizeMedia = (data: IMediaData): IManualMedia => {
  return {
    description: (data?.description as string) || '',
    id: (data?.id as number) || 0,
    image: normalizeImage(data?.image),
  };
};

type IRowData = TObject & { columns: TObject[] };

const normalizeTableRow = (data: IRowData) => ({
  columns:
    data?.columns?.map((column) => ({
      id: (column?.id as number) || 0,
      text: column?.text || '',
    })) || [],
  id: (data?.id as number) || 0,
});

type ISectionContentData = TObject & {
  media: IMediaData[];
  rows: IRowData[];
};

const normalizeSectionContent = (
  data: ISectionContentData,
): IManualContent | IManualTable => {
  if (data?.__component === MANUAL_COMPONENT.TABLE) {
    return {
      accessType: (data?.accessType as PERMISSION_TYPE) || PERMISSION_TYPE.AND,
      id: (data?.id as number) || 0,
      permissions: (data?.permissions as string)?.split(',') as PERMISSIONS[],
      rows: data?.rows?.map(normalizeTableRow) || [],
      type: MANUAL_COMPONENT.TABLE,
    } as IManualTable;
  }

  return {
    accessType: (data?.accessType as PERMISSION_TYPE) || PERMISSION_TYPE.AND,
    description: (data?.description as string) || '',
    id: (data?.id as number) || 0,
    media: data?.media?.map?.(normalizeMedia) || [],
    permissions: (data?.permissions as string)?.split(',') as PERMISSIONS[],
    subTitle: (data?.subTitle as string) || '',
    title: (data?.title as string) || '',
    type: MANUAL_COMPONENT.SECTION,
  } as IManualContent;
};

type ISectionConfiguration = TObject & { icon: TObject };

const normalizeSectionConfiguration = (
  data: ISectionConfiguration,
): IManualSectionConfiguration => {
  return {
    accessType: (data?.accessType as PERMISSION_TYPE) || PERMISSION_TYPE.AND,
    icon: normalizeImage(data?.icon),
    id: (data?.id as number) || 0,
    permissions: (data?.permissions as string)?.split(',') as PERMISSIONS[],
    sectionType: (data?.sectionType as ENTITY) || '',
  };
};

type ISectionData = TObject & {
  configuration: ISectionConfiguration;
  content: ISectionContentData[];
};

const normalizeSection = (data: ISectionData): IManualSection => {
  return {
    configuration: normalizeSectionConfiguration(data?.configuration),
    content: data?.content?.map?.(normalizeSectionContent) || [],
    createdAt: (data?.createdAt as string) || '',
    id: (data?.id as number) || 0,
    locale: (data?.locale as string) || '',
    publishedAt: (data?.publishedAt as string) || '',
    title: (data?.title as string) || '',
    updatedAt: (data?.updatedAt as string) || '',
  };
};

export interface IUserManualData {
  pagination: TObject;
  results: ISectionData[];
}

export const normalizeUserManual = (data: IUserManualData): IUserManual => {
  return {
    pagination: {
      page: (data?.pagination?.page as number) || 0,
      pageCount: (data?.pagination?.pageCount as number) || 0,
      pageSize: (data?.pagination?.pageSize as number) || 0,
      total: (data?.pagination?.total as number) || 0,
    },
    sections: data?.results?.map(normalizeSection) || [],
  };
};
